/**
 * Remembers the route the user asked for so sign-in can return them to it.
 *
 * Sign-in itself is in-page and synchronous from the user's perspective, but
 * the app still owns this: only it knows which of its paths are real, and the
 * value has to survive the async round trip through signIn() and the
 * follow-up /api/v1/me fetch.
 *
 * sessionStorage, to match the auth session: this dies with the tab too.
 */
const KEY = 'tollit.returnTo';

/** Public auth routes. Returning to one of these after sign-in is a loop. */
const NOT_A_DESTINATION = [
  '/sign-in',
  '/forgot-password',
  '/signed-out',
  '/access-not-provisioned',
  '/auth-error',
];

/**
 * Only same-origin, absolute, single-slash paths. Anything else — a full URL, a
 * protocol-relative `//evil.example`, a path with a scheme — is discarded
 * rather than sanitised, because the value reaches this function from a URL and
 * a redirect target is exactly the kind of thing worth being boring about.
 */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  if (!path) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.includes('://') || path.includes('\\')) return false;
  return !NOT_A_DESTINATION.some((p) => path === p || path.startsWith(`${p}/`));
}

export function rememberReturnPath(path: string | null | undefined): void {
  if (!isSafeReturnPath(path)) return;
  try {
    window.sessionStorage.setItem(KEY, path);
  } catch {
    // A browser with storage blocked still signs in; it just lands on the
    // role's default route.
  }
}

/**
 * Reads without clearing. Deliberately split from clearReturnPath(): the value
 * is needed while deciding where to navigate, which happens during render, and
 * StrictMode renders twice. A read that also cleared would hand the second pass
 * a null and drop the deep link in development only — the kind of gap between
 * dev and production that costs an afternoon to find.
 */
export function peekReturnPath(): string | null {
  try {
    const value = window.sessionStorage.getItem(KEY);
    return isSafeReturnPath(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Clears it. Called from an effect once the destination has been decided: a
 * stale value must not survive to the next flow, and it has to be cleared on
 * every way out of sign-in, not only the one that used it.
 */
export function clearReturnPath(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage blocked; there was nothing to clear.
  }
}
